// src/pages/Boutique.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MapPin, Star, Tag, Flame, ArrowRight, Search, Loader2, Gift, Sparkles, Info } from 'lucide-react';
import { FaSatelliteDish } from 'react-icons/fa';
import T from '../components/i18n/T';
import { ForfeoNetworkBadges } from '../components/network/ForfeoNetworkBadges';

const CATEGORIES = ['Tous', 'Détente', 'Gastronomie', 'Activités', 'Séjours'];

type SatelliteData = {
  visibility_label: string;
  smoke_label: string;
  snow_label: string;
  cloud_label: string;
  last_update: string;
  image_url: string;
};

// ✅ MISE À JOUR : Connexion officielle à la Supabase Edge Function
const useGlobalSatelliteConditions = () => {
  const [data, setData] = useState<SatelliteData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchGlobalSat = async () => {
      setLoading(true);
      try {
        // Appel natif et sécurisé vers ton Edge Function Supabase avec l'adresse texte
        const { data: result, error: apiError } = await supabase.functions.invoke('satellite-intelligence', {
          body: { address: "Québec, QC, Canada" }
        });

        if (apiError) throw apiError;
        
        if (result) {
          setData(result);
        } else {
          throw new Error("Aucune donnée retournée par la fonction.");
        }
      } catch (err) {
        console.warn("Forfeo Satellite (Mode Secours / Attente de déploiement):", err);
        // Fallback visuel premium en attendant que la fonction soit en ligne sur Supabase
        setData({
          visibility_label: "Excellente (10km+)",
          smoke_label: "Aucune fumée",
          snow_label: "Traces (0-2cm)",
          cloud_label: "Ciel dégagé",
          last_update: new Date().toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }),
          image_url: ""
        });
      } finally {
        setLoading(false);
      }
    };
    fetchGlobalSat();
  }, []);

  return { data, loading };
};


const Boutique: React.FC = () => {
  const [forfaits, setForfaits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('Tous');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [isMagicMode, setIsMagicMode] = useState(false);
  const { data: globalSatData, loading: globalSatLoading } = useGlobalSatelliteConditions();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchForfaits();
  }, []);

  const fetchForfaits = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('boutique_forfaits')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setForfaits(data || []);
    } catch (error) {
      console.error("Erreur lors de la récupération des forfaits:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredForfaits = forfaits.filter(forfait => {
    const matchesCategory = activeCategory === 'Tous' || forfait.category === activeCategory;
    const matchesSearch = forfait.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          forfait.business_name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  let displayedForfaits = [...filteredForfaits];
  
  if (isMagicMode && globalSatData) {
    const isGoodWeather = globalSatData.cloud_label.toLowerCase().includes('dégagé') || 
                          globalSatData.visibility_label.toLowerCase().includes('excellente');
    
    displayedForfaits.sort((a, b) => {
      if (isGoodWeather) {
        if (a.category === 'Activités' && b.category !== 'Activités') return -1;
        if (a.category !== 'Activités' && b.category === 'Activités') return 1;
      } else {
        const indoorA = a.category === 'Détente' || a.category === 'Gastronomie';
        const indoorB = b.category === 'Détente' || b.category === 'Gastronomie';
        if (indoorA && !indoorB) return -1;
        if (!indoorA && indoorB) return 1;
      }
      return 0;
    });
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Header />

      {/* HERO SECTION */}
      <div className="bg-slate-900 text-white pt-24 pb-16 px-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-purple-500/20 text-purple-300 py-1 px-3 rounded-full text-xs font-black uppercase tracking-widest border border-purple-500/30 flex items-center gap-2">
              <Tag size={12} /> <T fr="Boutique Forfeo" en="Forfeo Shop" />
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
            <T fr="Des expériences uniques," en="Unique experiences," /> <br className="hidden md:block"/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
              <T fr="à des prix imbattables." en="at unbeatable prices." />
            </span>
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mb-6">
            <T fr="Soutenez l'économie locale tout en économisant. Découvrez nos forfaits exclusifs négociés directement avec nos partenaires du réseau Forfeo." en="Support the local economy while saving. Discover our exclusive packages negotiated directly with Forfeo network partners." />
          </p>

          <ForfeoNetworkBadges variant="full" className="mb-10" />

          {/* BARRE DE RECHERCHE */}
          <div className="relative max-w-xl">
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
              <Search className="text-slate-400" size={20} />
            </div>
            <input 
              type="text" 
              placeholder="Chercher un forfait, un restaurant, un spa..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white/10 border border-white/20 text-white placeholder-slate-400 rounded-2xl py-4 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white/20 transition-all backdrop-blur-md font-medium"
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex overflow-x-auto gap-3 pb-2 md:pb-0 scrollbar-hide">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setIsMagicMode(false); }}
                className={`whitespace-nowrap px-6 py-3 rounded-xl font-bold transition-all ${
                  activeCategory === cat && !isMagicMode
                    ? 'bg-slate-900 text-white shadow-md' 
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <button 
            onClick={() => {
              setIsMagicMode(!isMagicMode);
              if (!isMagicMode) setActiveCategory('Tous');
            }}
            disabled={globalSatLoading}
            className={`flex-shrink-0 flex items-center gap-2 px-6 py-3 rounded-xl font-black transition-all duration-300 shadow-sm border ${
              isMagicMode 
                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-transparent shadow-emerald-500/30 scale-105'
                : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300 hover:text-emerald-600'
            }`}
          >
            {globalSatLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Sparkles size={18} className={isMagicMode ? 'text-white' : 'text-emerald-500'} />
            )}
            Où aller aujourd'hui ?
          </button>
        </div>

        {isMagicMode && globalSatData && (
          <div className="mb-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 md:p-8 text-white shadow-xl relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="absolute right-0 top-0 opacity-10 translate-x-1/4 -translate-y-1/4 pointer-events-none">
              <FaSatelliteDish size={200} />
            </div>
            <div className="relative z-10">
              <h3 className="text-xl md:text-2xl font-black mb-2 flex items-center gap-3">
                <FaSatelliteDish className="text-emerald-400" />
                <T fr="Intelligence Forfeo Satellite" en="Forfeo Satellite Intelligence" />
              </h3>
              <p className="text-slate-300 text-sm md:text-base max-w-2xl font-medium leading-relaxed">
                <T fr="Le catalogue est actuellement trié en temps réel selon les conditions observées dans la région." en="The catalog is currently sorted in real time based on observed conditions in the region." /> 
                <span className="block mt-1 text-emerald-400 font-bold">
                  Observation actuelle : {globalSatData.cloud_label} • Visibilité {globalSatData.visibility_label}
                </span>
              </p>
            </div>
            <div className="relative z-10 shrink-0 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 text-center">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1"><T fr="Mise à jour" en="Updated" /></div>
              <div className="text-2xl font-black">{globalSatData.last_update}</div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 size={40} className="text-purple-600 animate-spin mb-4" />
            <p className="text-slate-500 font-bold"><T fr="Chargement des forfaits..." en="Loading packages..." /></p>
          </div>
        ) : displayedForfaits.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-slate-200">
            <h3 className="text-xl font-bold text-slate-900 mb-2"><T fr="Aucun forfait trouvé" en="No package found" /></h3>
            <p className="text-slate-500"><T fr="Essayez de modifier vos filtres de recherche." en="Try adjusting your search filters." /></p>
            <button 
              onClick={() => {setSearchQuery(''); setActiveCategory('Tous'); setIsMagicMode(false);}}
              className="mt-6 text-purple-600 font-bold hover:underline"
            >
              <T fr="Réinitialiser la recherche" en="Reset search" />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayedForfaits.map((forfait, index) => {
              const isRecommended = isMagicMode && index < 3;

              return (
                <Link 
                  to={`/boutique/${forfait.id}`} 
                  key={forfait.id}
                  className={`bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group flex flex-col h-full transform hover:-translate-y-1 relative border ${
                    isRecommended ? 'border-emerald-400 ring-4 ring-emerald-500/10' : 'border-slate-100'
                  }`}
                >
                  <div className="relative h-56 overflow-hidden bg-slate-200">
                    <img 
                      src={forfait.image} 
                      alt={forfait.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    
                    <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1.5 rounded-lg font-black text-sm shadow-lg flex items-center gap-1 z-10">
                      <Flame size={14} /> -{forfait.discount}%
                    </div>
                    
                    <div className="absolute top-14 right-4 bg-pink-500 text-white px-2.5 py-1.5 rounded-lg font-black text-xs shadow-lg flex items-center gap-1 z-10 opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all">
                      <Gift size={12} /> <T fr="Offrable" en="Giftable" />
                    </div>

                    {isRecommended && globalSatData && (
                      <div className="absolute bottom-4 left-4 right-4 bg-emerald-500/95 backdrop-blur-md text-white p-3 rounded-xl shadow-lg border border-emerald-400 z-10 flex flex-col gap-1">
                        <div className="flex items-center gap-1.5 font-black text-[10px] uppercase tracking-widest mb-1">
                          <FaSatelliteDish size={12} className="animate-pulse" /> <T fr="Recommandé Actuellement" en="Currently Recommended" />
                        </div>
                        <div className="flex items-center justify-between text-[10px] font-bold text-emerald-50">
                          <span>{globalSatData.cloud_label}</span>
                          <span>Visibilité: {globalSatData.visibility_label.split(' ')[0]}</span>
                        </div>
                      </div>
                    )}

                    {(typeof forfait.tags === 'string' ? JSON.parse(forfait.tags) : forfait.tags || []).map((tag: string, idx: number) => (
                      <div key={idx} className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-slate-900 px-3 py-1 rounded-lg font-bold text-xs shadow-sm">
                        {tag}
                      </div>
                    ))}
                  </div>

                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-black uppercase tracking-wider text-purple-600">
                        {forfait.category}
                      </span>
                      <div className="flex items-center gap-1 text-sm font-bold text-slate-700">
                        <Star size={14} className="text-yellow-400 fill-yellow-400" />
                        {forfait.rating} <span className="text-slate-400 font-normal">({forfait.reviews})</span>
                      </div>
                    </div>

                    <h3 className="text-xl font-black text-slate-900 mb-2 line-clamp-2 leading-tight">
                      {forfait.title}
                    </h3>
                    
                    <p className="text-slate-600 font-medium mb-1 line-clamp-1">{forfait.business_name}</p>
                    
                    <div className="flex items-center gap-1 text-slate-400 text-sm mb-4">
                      <MapPin size={14} /> {forfait.location}
                    </div>

                    <ForfeoNetworkBadges variant="compact" className="mb-5" />

                    <div className="mt-auto pt-4 border-t border-slate-100 flex items-end justify-between">
                      <div>
                        <p className="text-sm text-slate-400 line-through decoration-red-400/50 decoration-2 font-medium">
                          {forfait.original_price}$
                        </p>
                        <p className="text-3xl font-black text-slate-900 leading-none">
                          {forfait.price}$
                        </p>
                      </div>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors text-white ${
                        isRecommended ? 'bg-emerald-500 group-hover:bg-emerald-600' : 'bg-slate-900 group-hover:bg-purple-600'
                      }`}>
                        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Boutique;
